import { __ } from '@99designs/i18n';
import { createCategory } from './helper';
import type { SupportedLocale } from '@99designs/i18n';
import type { DesignProductPresenter } from '../../../../products/';

export type Category = {
    id: string;
    name: string;
    products: DesignProductPresenter[];
    pageConfig?: CategoryPageConfig;
};

export type CategoryPageConfig = {
    path: string;
    availableLocales: SupportedLocale[];
};

export const getDefaultCategories = (
    productPresenters: Record<string, DesignProductPresenter>
): Record<string, Category> => ({
    logoAndBrand: createCategory('logo-and-brand', __('Logo & Brand'), [
        productPresenters.logoEssential,
        productPresenters.standardBusinessCards,
        productPresenters.flyers,
        productPresenters.letterhead,
        productPresenters.bellaCanvasUnisexShortSleeveJerseyTShirtDtg2go,
        productPresenters.classicCottonToteBagSmall,
    ]),
    businessCards: createCategory('business-cards', __('Business Cards'), [
        productPresenters.standardBusinessCards,
        productPresenters.roundedCornerBusinessCards,
        productPresenters.glossyBusinessCards,
        productPresenters.matteBusinessCards,
        productPresenters.embossedGlossBusinessCards,
        productPresenters.raisedFoilBusinessCards,
        productPresenters.premiumPlusBusinessCards,
        productPresenters.foilAccentBusinessCards,
    ]),
    packaging: createCategory(
        'packaging',
        __('Packaging'),
        [
            productPresenters.displayMailers,
            productPresenters.hangTags,
            productPresenters.standUpPouches,
            productPresenters.productBoxes,
            productPresenters.paperBags,
        ],
        { path: '/design/packaging', availableLocales: ['en-US', 'es-US'] }
    ),
    marketingMaterials: createCategory('marketing-materials', __('Marketing Materials'), [
        productPresenters.flyers,
        productPresenters.postcards,
        productPresenters.doorHangers,
        productPresenters.rackCards,
        productPresenters.presentationFolders,
        productPresenters.mailingServicesPostcard,
    ]),
    signsAndPosters: createCategory('signs-and-posters', __('Signs & Posters'), [
        productPresenters.vinylBanners,
        productPresenters.retractableBanners,
        productPresenters.yardSigns,
        productPresenters.payPalCarDoorMagnetsNa,
        productPresenters.posters,
        productPresenters.foamBoards,
        productPresenters.flags,
        productPresenters.tablecloths,
    ]),
    stationery: createCategory('stationery', __('Stationery'), [
        productPresenters.letterhead,
        productPresenters.notepads,
        productPresenters.giftCertificate,
        productPresenters.selfInkingStamps,
        productPresenters.stickyNotebookNa,
        productPresenters.notebooks,
        productPresenters.circleSelfInkingStamps,
        productPresenters.preInkedStamps,
    ]),
    banners: createCategory('banners', __('Banners'), [
        productPresenters.vinylBanners,
        productPresenters.retractableBanners,
        productPresenters.meshBanners,
        productPresenters.fabricBanners,
        productPresenters.stepAndRepeatBanners,
        productPresenters.doubleSidedBanners,
        productPresenters.customSizeVinylBanners,
        productPresenters.xBanners,
    ]),
    labelsAndStickers: createCategory('labels-and-stickers', __('Labels & Stickers'), [
        productPresenters.customSheetStickers,
        productPresenters.rollLabels,
        productPresenters.returnAddressLabels,
        productPresenters.stickerSingles,
        productPresenters.productLabels,
        productPresenters.circleStickers,
        productPresenters.rectangleStickers,
        productPresenters.plasticLabels,
    ]),
    clothingAndBags: createCategory('clothing-and-bags', __('Clothing & Bags'), [
        productPresenters.bellaCanvasUnisexShortSleeveJerseyTShirtDtg2go,
        productPresenters.standardEmbroideredHats,
        productPresenters.classicCottonToteBagSmall,
        productPresenters.carharttMidweightHoodedSweatshirtNa,
        productPresenters.ultraCottonYouthTShirt,
        productPresenters.vistaprintCuffedBeanie,
        productPresenters.jerzeesPolo,
        productPresenters.vistaprintPremiumLaptopBackpack15,
    ]),
    wedding: createCategory('wedding', __('Wedding'), [
        productPresenters.weddingInvitations,
        productPresenters.foamBoardWeddingSigns,
        productPresenters.weddingMenus,
        productPresenters.naWeddingCelebrationInvitations,
        productPresenters.weddingPrograms,
        productPresenters.weddingRsvpCards,
        productPresenters.weddingEnclosureCards,
        productPresenters.weddingThankYouCards,
    ]),
    shippingBoxesMailers: createCategory('shipping-boxes-mailers', __('Shipping Boxes & Mailers'), [
        productPresenters.displayMailers,
        productPresenters.productBoxes,
        productPresenters.shippingEnvelopes,
        productPresenters.shippingBoxes,
        productPresenters.packagingTape,
        productPresenters.polyMailers,
        productPresenters.squareMailer,
        productPresenters.tuckTopMailerBoxes,
    ]),
    packagingAccessoriesTags: createCategory(
        'packaging-accessories-tags',
        __('Packaging Accessories & Tags'),
        [
            productPresenters.packagingTape,
            productPresenters.customRibbon,
            productPresenters.hangTags,
            productPresenters.foldedHangtags,
            productPresenters.polytape,
            productPresenters.customTissuePaper,
            productPresenters.naWrappingPaper,
            productPresenters.packaginginsertcards,
        ]
    ),
    customPouches: createCategory('custom-pouches', __('Custom Pouches'), [
        productPresenters.standUpPouches,
        productPresenters.flatPouches,
    ]),
    foodPackaging: createCategory('food-packaging', __('Food Packaging'), [
        productPresenters.paperBags,
        productPresenters.customCoffeeSleeves,
        productPresenters.plasticCups,
        productPresenters.foodPackagingLabels,
        productPresenters.wineLabels,
        productPresenters.beerLabels,
        productPresenters.wineBags,
        productPresenters.treatBags,
    ]),
    productBoxes: createCategory('product-boxes', __('Product Boxes'), [
        productPresenters.tuckTopboxes,
        productPresenters.customPizzaBoxes,
        productPresenters.customGableBox,
        productPresenters.soapBoxes,
        productPresenters.lockBottomBoxes,
        productPresenters.autoLockBottomBoxes,
        productPresenters.chocolateBarBoxes,
        productPresenters.pillowBoxes,
    ]),
    shoppingBags: createCategory('shopping-bags', __('Shopping Bags'), [
        productPresenters.premiumFullColorPrintedBags,
        productPresenters.luxuryGiftBags,
        productPresenters.dieCutHandleBags,
    ]),
    shippingLabels: createCategory('shipping-labels', __('Shipping Labels'), [
        productPresenters.returnAddressLabels,
        productPresenters.envelopeSeals,
        productPresenters.packagingLabels,
        productPresenters.mailingLabels,
    ]),
});
